import { PARTNER_LEGAL_LINKS } from '@houzy/schemas/legal-documents';
import {
    CHECKOUT_WORDING_KEYS,
    checkoutNoteEntries,
} from '@houzy/schemas/partner-management/partner-subscription-management';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export type CheckoutTermsConsentProps = {
    commitmentLengthInMonths: number;
    autoProlongation: boolean;
    accepted: boolean;
    onAcceptedChange: (accepted: boolean) => void;
    showError: boolean;
};

function Note({ children }: { children: ReactNode }): ReactElement {
    return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-start' }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', flexShrink: 0 }} />
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500, fontSize: 11, letterSpacing: '0.3px' }}>
                {children}
            </Typography>
        </Stack>
    );
}

export function CheckoutTermsConsent({
    commitmentLengthInMonths,
    autoProlongation,
    accepted,
    onAcceptedChange,
    showError,
}: CheckoutTermsConsentProps): ReactElement {
    const { t } = useTranslation();

    return (
        <Stack spacing={1}>
            {checkoutNoteEntries({ autoProlongation, commitmentLengthInMonths }).map(({ key, params }) => (
                <Note key={key}>
                    <Trans
                        i18nKey={key}
                        values={params}
                        components={{
                            strong: (
                                <Typography
                                    component="span"
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{ fontSize: 11, letterSpacing: '0.3px' }}
                                />
                            ),
                        }}
                    />
                </Note>
            ))}

            <FormControlLabel
                control={
                    <Checkbox
                        size="small"
                        color="primary"
                        checked={accepted}
                        onChange={(event) => onAcceptedChange(event.target.checked)}
                    />
                }
                label={
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500, fontSize: 11, letterSpacing: '0.3px' }}>
                        <Trans
                            i18nKey={CHECKOUT_WORDING_KEYS.declaration}
                            components={{
                                termsLink: (
                                    <Link
                                        href={PARTNER_LEGAL_LINKS.termsOfService}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="always"
                                        color="primary"
                                    />
                                ),
                                privacyLink: (
                                    <Link
                                        href={PARTNER_LEGAL_LINKS.dataPolicy}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="always"
                                        color="primary"
                                    />
                                ),
                            }}
                        />
                    </Typography>
                }
            />
            {showError && <FormHelperText error>{t('CHECKOUT.NOTES.ACCEPT_TERMS_REQUIRED')}</FormHelperText>}
        </Stack>
    );
}
